/**
 * nutritionSessionTimes — Pure FE helpers for nutrition session time validation.
 *
 * Strict rules (no grace window):
 *   startError — start strictly after now  → 'startInFuture'
 *   endError   — end strictly after now    → 'endInFuture'
 *              — end strictly before start → 'endBeforeStart'  (end === start OK)
 *
 * Works with datetime-local strings ("2026-06-27T14:30") or ISO strings.
 * Empty or unparseable input → null (no error) so required-field handling stays separate.
 * endInFuture is checked BEFORE endBeforeStart (mirrors backend ordering).
 *
 * Both functions accept an optional injected `now` (Date) for testability.
 */

export type StartErrorCode = 'startInFuture';
export type EndErrorCode = 'endInFuture' | 'endBeforeStart';

/**
 * Validate a session start time.
 * Returns a code if invalid, null if valid or empty/unparseable.
 */
export function startError({
  startedAt,
  now = new Date(),
}: {
  startedAt: string;
  now?: Date;
}): StartErrorCode | null {
  if (!startedAt) return null;
  const ms = new Date(startedAt).getTime();
  if (Number.isNaN(ms)) return null;
  if (ms > now.getTime()) return 'startInFuture';
  return null;
}

/**
 * Validate a session end time against "now" and the persisted start.
 * Returns a code if invalid, null if valid or empty/unparseable.
 *
 * @param startedAt - ISO string of the persisted start time (optional)
 * @param endedAt   - datetime-local or ISO string entered by the user
 * @param now       - injectable current time (defaults to `new Date()`)
 */
export function endError({
  startedAt,
  endedAt,
  now = new Date(),
}: {
  startedAt?: string;
  endedAt: string;
  now?: Date;
}): EndErrorCode | null {
  if (!endedAt) return null;
  const endMs = new Date(endedAt).getTime();
  if (Number.isNaN(endMs)) return null;

  // Check endInFuture BEFORE endBeforeStart
  if (endMs > now.getTime()) return 'endInFuture';

  if (startedAt) {
    const startMs = new Date(startedAt).getTime();
    if (!Number.isNaN(startMs) && endMs < startMs) return 'endBeforeStart';
  }

  return null;
}
